import {
  XStack,
  SizableText,
  YStack,
  Stack,
  YStackProps,
  TextArea,
  useMedia,
} from '@mythical/ui'
import { color } from '@mythical/ui'
import useMutation from 'app/hooks/use-mutation'
import {
  createCommentMutation,
  editCommentMutation,
  editCommentMutationResult,
  commentMutationResult,
} from 'app/mutations/comments/comment'
import { useStores } from 'app/store'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'

import { Avatar } from 'app/components/primitive/Avatar'
import { Button } from 'app/components/primitive/Button'

const createQuery = createCommentMutation
const editQuery = editCommentMutation

interface CommentFormProps {
  isReply?: boolean
  parent?: string
  id?: string
  isEditing?: true
  comment?: string
  onSubmitted?: () => void
  onCancel?: () => void
}

export const CommentForm = observer(
  ({
    parent,
    id,
    isEditing,
    isReply,
    comment,
    onSubmitted,
    onCancel,
    ...props
  }: CommentFormProps & YStackProps) => {
    const { userStore } = useStores()
    const media = useMedia()
    const query = isEditing ? editQuery : createQuery
    const format = isEditing ? editCommentMutationResult : commentMutationResult
    const [input, setInput] = useState(comment || '')

    const { loading, error, makeRequest } = useMutation(query, format)

    const handleCancel = () => {
      setInput('')
      onCancel?.()
    }

    if (!parent && !id) {
      return null
    }

    const handleSubmit = async () => {
      const variables = {
        comment: input.trim(),
        id: '',
        parent: '',
      }

      if (isEditing) {
        variables.id = id || ''
      } else {
        variables.parent = parent || ''
      }

      const result = await makeRequest(variables)

      if (result && result.id) {
        if (!isEditing) {
          setInput('')
        }
        userStore?.refreshAccessToken()
        onSubmitted?.()
      }
    }

    return (
      <YStack
        gap="$2"
        br="$3"
        bg="$bgSecondary"
        p="$3"
        $gtXs={{ p: '$3.5' }}
        {...props}
      >
        <XStack gap="$3" alignItems="flex-start">
          <Avatar
            src={userStore?.user?.profileImage}
            bw={2}
            boc="$fgBrand"
            width={40}
            height={40}
            {...((isReply || isEditing) &&
              media.xs && { width: 24, height: 24 })}
          />
          <YStack fg={1} fs={1} gap="$2">
            <TextArea
              theme="form"
              placeholderTextColor={color.fgSecondary}
              placeholder={isReply ? 'Reply...' : 'Add a comment...'}
              flex={1}
              size="$2"
              px="$2.5"
              py="$2"
              value={input}
              onChangeText={setInput}
              onSubmitEditing={handleSubmit}
            />

            <YStack jc="flex-start" gap="$2" fw="wrap">
              {input !== '' && (
                <>
                  <Stack flex={1} jc="center">
                    {error && (
                      <SizableText my="$1.5" color="$fgError">
                        {error.message}
                      </SizableText>
                    )}
                  </Stack>
                  <XStack jc="flex-end" gap="$2">
                    <Button size="$3" theme="secondary" onPress={handleCancel}>
                      Cancel
                    </Button>
                    <Button
                      size="$3"
                      scaleSpace={0.4}
                      onPress={handleSubmit}
                      loading={loading}
                    >
                      Submit
                    </Button>
                  </XStack>
                </>
              )}
            </YStack>
          </YStack>
        </XStack>
      </YStack>
    )
  },
)
